import React, { useState, useEffect } from "react";
import apiClient from "../../Api/apiConfig";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useAuth } from "../../lib/auth";
import {
  Button,
  Input,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "../UIComponents";
import moment from "moment";
import "moment/locale/fr";
import "../../styles/MembersTable.css";
import Modal from "react-modal";

Modal.setAppElement("#root");

const columnHelper = createColumnHelper();

const AdminAppointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  const [formData, setFormData] = useState({
    user_id: "",
    start_time: "",
    end_time: "",
    appointment_type: "",
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // ✅ baseURL = /api => ici on met juste les chemins "métiers"
      const [resAppointments, resUsers] = await Promise.all([
        apiClient.get("/appointments"),
        apiClient.get("/users"),
      ]);

      setAppointments(resAppointments.data || []);
      setUsers(resUsers.data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des données:", err);
      const msg =
        err?.message ||
        err?.error ||
        "Impossible de charger les données. Veuillez réessayer plus tard.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Suppression d'un rendez-vous
  const handleDelete = async (id) => {
    if (!id) {
      alert("Erreur : ID du rendez-vous introuvable.");
      return;
    }
    try {
      await apiClient.delete(`/appointments/${id}`);
      await fetchData();
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      alert("Une erreur s'est produite lors de la suppression.");
    }
  };

  // Ouvrir / fermer les modals
  const openEditModal = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      user_id: appointment.user_id,
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      appointment_type: appointment.appointment_type,
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingAppointment(null);
    setFormData({
      user_id: "",
      start_time: "",
      end_time: "",
      appointment_type: "",
    });
  };

  const openAddModal = () => {
    setFormData({
      user_id: "",
      start_time: "",
      end_time: "",
      appointment_type: "",
    });
    setShowAddModal(true);
  };

  // Ajout d'un rendez-vous
  const handleAddAppointment = async () => {
    try {
      await apiClient.post("/appointments", formData);
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      console.error("Erreur lors de la création:", err);
      alert("Une erreur s'est produite lors de la création.");
    }
  };

  // Modification d'un rendez-vous
  const handleEditAppointment = async () => {
    if (!editingAppointment) return;
    try {
      await apiClient.put(`/appointments/${editingAppointment.id}`, formData);
      setShowEditModal(false);
      setEditingAppointment(null);
      await fetchData();
    } catch (err) {
      console.error("Erreur lors de la mise à jour:", err);
      alert("Une erreur s'est produite lors de la mise à jour.");
    }
  };

  // Confirm suppression
  const confirmDeleteAppointment = (appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteConfirm(true);
  };

  const handleDeleteAppointment = async () => {
    if (appointmentToDelete) {
      await handleDelete(appointmentToDelete.id);
      setShowDeleteConfirm(false);
      setAppointmentToDelete(null);
    }
  };

  const columns = [
    columnHelper.accessor("user_id", {
      header: "Utilisateur",
      cell: (info) => {
        const appointment = info.row.original;
        const userObj = (users || []).find((u) => u.id === appointment.user_id);
        return userObj && userObj.first_name
          ? userObj.first_name
          : `#${appointment.user_id}`;
      },
    }),
    columnHelper.accessor("start_time", {
      header: "Début",
      cell: (info) =>
        info.getValue()
          ? moment(info.getValue()).format("DD/MM/YYYY HH:mm")
          : "",
    }),
    columnHelper.accessor("end_time", {
      header: "Fin",
      cell: (info) =>
        info.getValue()
          ? moment(info.getValue()).format("DD/MM/YYYY HH:mm")
          : "",
    }),
    columnHelper.accessor("appointment_type", {
      header: "Type",
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor("id", {
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <>
              <Button onClick={() => openEditModal(info.row.original)}>
                Modifier
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  confirmDeleteAppointment(info.row.original)
                }
              >
                Supprimer
              </Button>
            </>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: appointments,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <p>Chargement...</p>;
  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-4">Gestion des Rendez-vous</h1>

      <Button onClick={openAddModal}>Ajouter un Rendez-vous</Button>

      <Table className="mt-4">
        <TableHead>
          <TableRow>
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <TableCell key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Modal ajout */}
      {showAddModal && (
        <Modal
          isOpen={showAddModal}
          onRequestClose={() => setShowAddModal(false)}
        >
          <h2>Ajouter un Rendez-vous</h2>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <Input
              name="user_id"
              placeholder="User ID"
              value={formData.user_id}
              onChange={handleFormChange}
            />
            <Input
              name="start_time"
              type="datetime-local"
              value={formData.start_time}
              onChange={handleFormChange}
            />
            <Input
              name="end_time"
              type="datetime-local"
              value={formData.end_time}
              onChange={handleFormChange}
            />
            <Input
              name="appointment_type"
              placeholder="Type"
              value={formData.appointment_type}
              onChange={handleFormChange}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleAddAppointment}>Créer</Button>
            <Button onClick={() => setShowAddModal(false)}>Annuler</Button>
          </div>
        </Modal>
      )}

      {/* Modal édition */}
      {showEditModal && (
        <Modal
          isOpen={showEditModal}
          onRequestClose={closeEditModal}
        >
          <h2>Modifier le Rendez-vous</h2>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <Input
              name="user_id"
              placeholder="User ID"
              value={formData.user_id}
              onChange={handleFormChange}
            />
            <Input
              name="start_time"
              type="datetime-local"
              value={formData.start_time}
              onChange={handleFormChange}
            />
            <Input
              name="end_time"
              type="datetime-local"
              value={formData.end_time}
              onChange={handleFormChange}
            />
            <Input
              name="appointment_type"
              placeholder="Type"
              value={formData.appointment_type}
              onChange={handleFormChange}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleEditAppointment}>Enregistrer</Button>
            <Button onClick={closeEditModal}>Annuler</Button>
          </div>
        </Modal>
      )}

      {/* Modal confirmation suppression */}
      {showDeleteConfirm && (
        <Modal
          isOpen={showDeleteConfirm}
          onRequestClose={() => setShowDeleteConfirm(false)}
        >
          <h2>Confirmation de suppression</h2>
          <p>Êtes-vous sûr de vouloir supprimer ce rendez-vous ?</p>
          <div className="mt-4 flex gap-2">
            <Button variant="destructive" onClick={handleDeleteAppointment}>
              Supprimer
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)}>
              Annuler
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminAppointments;
